import { I18n } from "@assets/strings";
import React from "react";
import { Link } from "react-router";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useIsLearner, useIsPerson } from "@domain/currentProfile/hooks";
import { usePersonQuizzes } from "@domain/lms/quiz/hooks";
import { Person } from "@domain/user/user";
import { fullName } from "@domain/user/utils";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { Secondary } from "@ui/components/containers/textBlocks";
import { InfiniteScroll } from "@ui/components/infiniteScroll";
import { Stack } from "@ui/components/layout";
import { QuizThumbnail } from "@ui/components/quiz/quizThumbnail";
import { desktop, gap, mobile } from "@ui/styles/utils";

interface ProfileQuizzesListProps extends React.HTMLAttributes<HTMLDivElement> {
	user: Person;
	isTeacher?: boolean;
	organizationName?: string;
}

export const ProfileQuizzesList: React.FC<ProfileQuizzesListProps> = ({
	user,
	isTeacher,
	organizationName,
	...htmlProps
}) => {
	const isOrganization = user.type === "COMPANY" || user.type === "INSTITUTE";
	const { result: quizzes, fetchMore, loading, totalElements, moreLoading } = usePersonQuizzes(user.id, isOrganization);
	const isAuthor = useIsPerson(user);
	const isLearner = useIsLearner();

	return totalElements === 0 && !loading ? (
		<Stack gap={10}>
			 <Secondary>{I18n.t("profile.quizzes.empty", { user: fullName(user) })}</Secondary>
			{isTeacher && !isLearner && isOrganization && organizationName ? (
				<Link to={Path.QuizEditor("new")}>
					<PrimaryButton>{I18n.t(`organization.quizze.incentive`, { institute: organizationName })}</PrimaryButton>
				</Link>
			) : null}
		</Stack>
	) : (
		<InfiniteScroll onEndReached={fetchMore} loading={loading || moreLoading}>
			<Quizzes {...htmlProps}>
				{quizzes.map((quiz) => (quiz ? <QuizThumbnail key={quiz.id} quiz={quiz} displayStatus={isAuthor} /> : <></>))}
				{loading || (moreLoading && <ClipLoader />)}
			</Quizzes>
		</InfiniteScroll>
	);
};

const Quizzes = styled.div`
	@media ${desktop} {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		grid-gap: 20px;
	}
	@media ${mobile} {
		${gap(20)};
	}
`;
